import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { BellOff, Send, Unlink } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

/**
 * Enlazar el Telegram propio y decidir cuánto interrumpe.
 *
 * ⚠ **El código se vincula desde el bot, no copiando un número.** La alternativa era
 * que cada quien le escribiera a `@userinfobot`, copiara su chat id de nueve cifras y
 * lo pegara aquí: en una agencia pequeña eso acaba haciéndolo una sola persona por
 * todos, y basta una cifra mal copiada para que los avisos de alguien se vayan al
 * chat de otro sin que nadie lo note.
 *
 * ⚠ **El nivel existe para que no se silencie el bot.** Un canal que manda cada
 * comentario de cada tarea acaba silenciado en dos semanas, y entonces tampoco llega
 * lo que sí había que atender. La campanita de aquí al lado lo recibe todo igual.
 *
 * ⚠ **El enlace tarda unos segundos en confirmarse.** El worker lee el buzón del bot
 * cada diez segundos, así que esto se relee mientras hay un código vivo — sin eso,
 * quien escribe al bot vuelve aquí, ve «sin enlazar» y pide otro código.
 */

const NIVELES = [
  {
    valor: "IMPORTANTE",
    etiqueta: "Sólo lo importante",
    ayuda: "Te asignaron algo o cambió tu encargo",
  },
  { valor: "TODO", etiqueta: "Todo", ayuda: "También estados, avances y comentarios" },
  { valor: "NINGUNO", etiqueta: "Nada", ayuda: "Sólo la campanita de aquí" },
] as const

type Nivel = (typeof NIVELES)[number]["valor"]

type Estado = { vinculado: boolean; nivel: Nivel; avisarme_lo_propio: boolean; bot: string }

export function Telegram() {
  const [enlace, setEnlace] = useState<{ codigo: string; enlace: string } | null>(null)
  const clienteQuery = useQueryClient()

  const estado = useQuery({
    queryKey: ["perfil", "telegram"],
    queryFn: () => api.get<Estado>("/api/v1/perfil/telegram"),
    // Mientras hay un código vivo se comprueba seguido: el enlace lo confirma el
    // worker, no esta petición, y son unos segundos de espera con el móvil delante.
    refetchInterval: (consulta) => {
      // Sólo mientras se espera al bot: con el enlace ya hecho, seguir preguntando
      // cada tres segundos es una petición por usuario y por pantalla, para siempre.
      const datos = consulta.state.data
      return enlace !== null && datos?.vinculado !== true ? 3_000 : false
    },
  })

  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["perfil", "telegram"] })

  const pedir = useMutation({
    mutationFn: () =>
      api.post<{ codigo: string; enlace: string }>("/api/v1/perfil/telegram/codigo"),
    onSuccess: (r) => setEnlace({ codigo: r.codigo, enlace: r.enlace }),
  })

  const cambiar = useMutation({
    mutationFn: (nivel: Nivel) => api.patch("/api/v1/perfil/telegram", { nivel }),
    onSuccess: refrescar,
  })

  // ⚠ Vale para la campanita Y para Telegram: es la regla de a quién se avisa, no el
  // canal. Por eso se ofrece también sin el bot enlazado.
  const propio = useMutation({
    mutationFn: (avisarme_lo_propio: boolean) =>
      api.patch("/api/v1/perfil/telegram", { avisarme_lo_propio }),
    onSuccess: refrescar,
  })

  const soltar = useMutation({
    mutationFn: () => api.delete("/api/v1/perfil/telegram"),
    onSuccess: () => {
      setEnlace(null)
      refrescar()
    },
  })

  const vinculado = estado.data?.vinculado === true

  return (
    <section className="tarjeta mt-6">
      <h2 className="flex items-center gap-2 text-sm font-medium">
        <Send size={14} aria-hidden={true} />
        Telegram
      </h2>

      {estado.isPending && (
        <p role="status" className="mt-2 text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}

      {estado.data && !vinculado && (
        <>
          <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
            Recibe tus avisos en el móvil. Pide un código y escríbeselo al bot.
          </p>
          {enlace === null ? (
            <button
              type="button"
              onClick={() => pedir.mutate()}
              disabled={pedir.isPending}
              className="boton-primario mt-3 px-3 py-1.5 text-sm disabled:opacity-50"
            >
              {pedir.isPending ? "Generando…" : "Enlazar mi Telegram"}
            </button>
          ) : (
            <div
              className="mt-3 rounded-[var(--radius-control)] border border-dashed p-3"
              style={{ borderColor: "var(--border)" }}
            >
              {/* ⚠ El enlace primero, y el código debajo. `t.me/<bot>?start=CODIGO`
                  abre el chat y manda la orden de un toque; sin él hay que encontrar
                  el bot por su nombre y teclear ocho caracteres, que es donde se
                  pierde la gente. El texto queda para el escritorio, donde el enlace
                  abre una pestaña que a veces no lleva a ninguna parte. */}
              {enlace.enlace !== "" && (
                <a
                  href={enlace.enlace}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="enlace-telegram"
                  className="boton-primario inline-flex items-center gap-2 px-3 py-1.5 text-sm"
                >
                  <Send size={14} aria-hidden={true} />
                  Abrir Telegram y enlazar
                </a>
              )}
              <p className={enlace.enlace === "" ? "text-sm" : "mt-2 text-sm"}>
                {enlace.enlace === "" ? "Escríbele al bot: " : "O escríbele al bot: "}
                <code className="tabular-nums font-medium" data-testid="codigo-telegram">
                  /vincular {enlace.codigo}
                </code>
              </p>
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                Caduca en quince minutos. Esta pantalla se actualiza sola en cuanto el bot te
                reconozca.
              </p>
            </div>
          )}
          {pedir.isError && (
            <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
              {pedir.error instanceof ErrorApi
                ? pedir.error.message
                : "No se pudo generar el código."}
            </p>
          )}
        </>
      )}

      {vinculado && (
        <>
          <p data-testid="telegram-vinculado" className="mt-1 text-sm">
            Enlazado. Recibes avisos en Telegram.
          </p>

          <fieldset className="mt-3">
            <legend className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Cuánto te interrumpe
            </legend>
            <div className="mt-1 space-y-1">
              {NIVELES.map((n) => (
                <label key={n.valor} className="flex items-start gap-2 text-sm">
                  <input
                    type="radio"
                    name="nivel-telegram"
                    className="mt-1"
                    checked={estado.data?.nivel === n.valor}
                    disabled={cambiar.isPending}
                    onChange={() => cambiar.mutate(n.valor)}
                  />
                  <span>
                    {n.etiqueta}
                    <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                      {n.ayuda}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <button
            type="button"
            onClick={() => soltar.mutate()}
            disabled={soltar.isPending}
            className="boton-secundario mt-3 flex items-center gap-2 px-3 py-1.5 text-sm disabled:opacity-50"
          >
            {estado.data?.nivel === "NINGUNO" ? (
              <BellOff size={14} aria-hidden={true} />
            ) : (
              <Unlink size={14} aria-hidden={true} />
            )}
            Desenlazar
          </button>
        </>
      )}

      {estado.data && (
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4"
            data-testid="avisarme-lo-propio"
            checked={estado.data.avisarme_lo_propio}
            disabled={propio.isPending}
            onChange={(e) => propio.mutate(e.target.checked)}
          />
          <span>
            Avisarme también de lo que hago yo
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              Te llega lo que te habría llegado si lo hubiera hecho otra persona: por ejemplo, la
              orden que aprobaste o pagaste tú. En la campanita y, si lo tienes enlazado, en
              Telegram.
            </span>
          </span>
        </label>
      )}
    </section>
  )
}
